// src/components/ValuePropositionSection.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion'; // Import motion

// --- VIDEO IMPORT ---
import localVideoPath from '../assets/nyc_community_demo.mp4'; 
const videoPosterPath = ""; 

// --- Placeholder Icons replaced with INLINE SVG ---
const CheckmarkIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
); 

const ExpandMoreIcon = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
);


// Content Arrays
const benefits = [
    "Real-time NYC community updates",
    "Neighborhood-based chat groups",
    "Safe place for local discussion",
    "No algorithm — everything is community driven",
    "Find people who live near you",
    "Connect with locals about transportation, events, public safety, food spots, etc."
];

const faqs = [
    { q: "Is this only for NYC?", a: "Yes, this platform is strictly for New Yorkers and content should be focused on the five boroughs. We aim to keep the conversations hyperlocal and relevant to city life." },
    { q: "Are chat rooms moderated?", a: "Yes, all chat rooms are monitored by a dedicated team of moderators who enforce our community guidelines to ensure a respectful and safe environment for all residents." },
    { q: "Is it free?", a: "Absolutely! Joining the NYC Community Hub, creating an account, and accessing all discussions and chat rooms is completely free." },
    { q: "What kind of conversations are allowed?", a: "We focus on topics related to city life: neighborhood news, public transit, local events, small business features, housing tips, and community organizing." },
];

// --- FRAMER MOTION VARIANTS ---

// Container for Staggered Benefits List
const benefitListContainer = {
    visible: {
        transition: { staggerChildren: 0.08, delayChildren: 0.3 }
    }
};

// Item for Benefit List
const benefitItem = {
    hidden: { x: -20, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.4 } }
};

// Container for FAQ Staggered Entrance
const faqContainer = {
    visible: {
        transition: { staggerChildren: 0.1 }
    }
};

// Item for FAQ Accordions
const faqItem = {
    hidden: { scale: 0.95, opacity: 0, y: 10 },
    visible: { scale: 1, opacity: 1, y: 0, transition: { duration: 0.3 } }
};

// Animation for the Video Column
const videoAnimation = {
    hidden: { x: 50, opacity: 0 },
    visible: { x: 0, opacity: 1, transition: { duration: 0.6, ease: "easeOut" } }
};


const ValuePropositionSection = () => {
    
    const [openFaq, setOpenFaq] = useState(null);

    const handleFaqToggle = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };
    
    return (
        <div className="value-prop-section-container">
            <div className="value-prop-content-wrapper">
                
                {/* --- 1. Why You Should Use It (Benefits & Video) --- */}
                <div className="value-prop-grid-container">
                    
                    <motion.div 
                        className="value-prop-benefits-column"
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, amount: 0.4 }}
                        transition={{ duration: 0.5 }}
                    >
                        <h3 className="value-prop-benefits-title">
                            Why Connect With Locals?
                        </h3>
                        <p className="value-prop-benefits-subtitle">
                            This is your dedicated space to talk about what's happening around you — in your city, not some global forum.
                        </p>
                        
                        <motion.ul 
                            className="value-prop-benefits-list"
                            variants={benefitListContainer}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true, amount: 0.5 }}
                        >
                            {benefits.map((benefit, index) => (
                                <motion.li 
                                    key={index} 
                                    className="value-prop-benefit-item"
                                    variants={benefitItem}
                                >
                                    <span className="value-prop-checkmark-icon">
                                        <CheckmarkIcon /> 
                                    </span>
                                    <span className="value-prop-benefit-text">{benefit}</span>
                                </motion.li>
                            ))}
                        </motion.ul>
                    </motion.div>
                    
                    <motion.div 
                        className="value-prop-video-column"
                        variants={videoAnimation}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.4 }}
                    >
                        <div className="value-prop-video-wrapper">
                            <video
                                className="value-prop-video-element"
                                src={localVideoPath}
                                poster={videoPosterPath}
                                controls
                                playsInline
                                muted
                                autoPlay
                            >
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    </motion.div>
                </div>

                <hr className="value-prop-divider" />
                
                {/* --- 2. FAQ Section --- */}
                <div className="value-prop-faq-section">
                    <motion.h3 
                        className="value-prop-faq-title"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        Frequently Asked Questions
                    </motion.h3>
                    
                    <motion.div 
                        className="value-prop-accordion-container"
                        variants={faqContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        {faqs.map((item, index) => (
                            <motion.div 
                                key={index} 
                                className={`value-prop-accordion-item ${openFaq === index ? 'value-prop-accordion-open' : ''}`}
                                variants={faqItem}
                            >
                                <button 
                                    className="value-prop-accordion-summary"
                                    onClick={() => handleFaqToggle(index)}
                                >
                                    <span className="value-prop-accordion-question">{item.q}</span>
                                    {/* Rotate the icon using Framer Motion's 'animate' property for smoothness */}
                                    <motion.span
                                        animate={{ rotate: openFaq === index ? 180 : 0 }}
                                        transition={{ duration: 0.2 }}
                                        className="value-prop-accordion-icon"
                                    >
                                        <ExpandMoreIcon /> 
                                    </motion.span>
                                </button>
                                {/* Use AnimatePresence for smooth showing/hiding */}
                                {openFaq === index && (
                                    <motion.div 
                                        className="value-prop-accordion-details"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <p className="value-prop-accordion-answer">{item.a}</p>
                                    </motion.div>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
                
            </div>
        </div>
    );
};

export default ValuePropositionSection;

